import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { useForm } from 'react-hook-form'

import { randomToastSuccess } from '@/shared/Toast'
import TipTapTextarea from '@/shared/TipTapTextarea'
import PaymentFields, { payEstimate, withoutHiddenPay } from '@/shared/PaymentFields'
import PrimaryButton from '@/shared/Buttons/Primary'
import DefaultButton from '@/shared/Buttons/Default'
import useQuery from '@/hooks/useQuery'
import { useGlobalState } from '@/state'
import { Required } from '@/pages/ManageProjects/ProjectEdit/RequiredInformation'
import RoleTags from '@/pages/ManageProjects/ProjectEdit/RoleTags'

const AUDITION_KINDS = ['Voice Actor', 'Singer', 'Actor']

// Inline role form. A new role saves once with name, description, and lines,
// then unlocks tags and a pay override (both need a role id). Existing roles
// get everything at once.
const RoleEdit = ({ initialRole, projectId, updateRole, roleKind, closeModal, onCreated }) => {
  const tipTapIdx = useRef(1)
  const [, setToast] = useGlobalState('toast')
  const [role, setRole] = useState(initialRole || {})
  const [overridePayment, setOverridePayment] = useState(false)
  const [payment, setPayment] = useState(initialRole.payment)
  const { putpostRequest } = useQuery()
  const form = useForm({
    defaultValues: {
      name: initialRole.name || '',
      characteristics: initialRole.characteristics,
      kind: initialRole.kind || roleKind,
      payment: initialRole.payment,
      line1: initialRole.line1,
      line2: initialRole.line2,
      line3: initialRole.line3,
      payment_details: initialRole.paymentDetails,
      payment_range: initialRole.paymentRange,
      payment_interval: initialRole.paymentInterval,
      payment_currency: initialRole.paymentCurrency,
      payment_deferred_type: initialRole.paymentDeferredType,
      payment_amount_min: initialRole.paymentAmountMin,
      payment_amount_max: initialRole.paymentAmountMax,
      expected_commitment_amount: initialRole.expectedCommitmentAmount,
      expected_commitment_interval: initialRole.expectedCommitmentInterval
    }
  })
  const { formState: { errors }, register, unregister, watch, handleSubmit, setValue, getValues, setError, setFocus } = form
  const [loading, setLoading] = useState(false)
  const characteristics = getValues().characteristics
  const kind = watch('kind')
  const isNew = typeof role.id === 'undefined'

  useEffect(() => { setFocus('name') }, [setFocus])

  useEffect(() => {
    register('characteristics')
    register('payment')
    register('payment_details')
    return () => { unregister('characteristics'); unregister('payment'); unregister('payment_details') }
  }, [register])

  const keyPress = (e) => {
    if (e.target.name === 'name' && e.keyCode === 13) { e.preventDefault() }
    if ((e.metaKey || e.ctrlKey) && e.keyCode === 13) { handleSubmit(onSubmit)() }
  }

  const handlePaymentChange = (value) => {
    setPayment(value)
    setValue('payment', value, { shouldDirty: true, shouldValidate: true })
  }

  const adoptPayment = (saved) => {
    setPayment(saved.payment)
    const map = {
      payment: saved.payment, payment_details: saved.paymentDetails, payment_range: saved.paymentRange,
      payment_interval: saved.paymentInterval, payment_currency: saved.paymentCurrency, payment_deferred_type: saved.paymentDeferredType,
      payment_amount_min: saved.paymentAmountMin, payment_amount_max: saved.paymentAmountMax,
      expected_commitment_amount: saved.expectedCommitmentAmount, expected_commitment_interval: saved.expectedCommitmentInterval
    }
    Object.entries(map).forEach(([k, v]) => setValue(k, v, { shouldDirty: false, shouldValidate: false }))
  }

  const onSubmit = (data) => {
    setLoading(true)
    const creating = isNew
    const url = creating ? '/api/v3/roles' : `/api/v3/roles/${role.id}`
    putpostRequest(url, creating ? 'POST' : 'PATCH', { role: { ...withoutHiddenPay(data), project_id: projectId } }, (err, jsonData) => {
      setLoading(false)
      if (err) {
        if (typeof err !== 'string') { Object.entries(err).forEach(([key, value]) => { setError(key, value) }) }
        return
      }
      setRole({ ...jsonData.role })
      if (creating) {
        adoptPayment(jsonData.role)
        if (onCreated) { onCreated(jsonData.role) }
        setToast(<div className='ml-3 w-0 flex-1 pt-0.5'>
          <p className='text-sm font-medium text-gray-800 dark:text-white'>Role created</p>
          <p className='mt-1 text-sm text-gray-500 dark:text-gray-100'>You're on a roll! ...okay, I'll stop.</p>
        </div>)
        return
      }
      if (typeof updateRole === 'function') { updateRole(jsonData.role) }
      setToast(randomToastSuccess())
    })
  }

  const estimate = payEstimate({
    paymentInterval: role.paymentInterval, paymentAmountMax: role.paymentAmountMax,
    expectedCommitmentAmount: role.expectedCommitmentAmount, expectedCommitmentInterval: role.expectedCommitmentInterval
  })
  const paySummary = payment === 'unpaid' ? 'Unpaid' : payment === 'deferred' ? 'Deferred pay' : estimate ? `Paid, about $${estimate.hourly} an hour` : 'Paid'

  return <div className='space-y-5'>
    <form className='space-y-5' onSubmit={(e) => e.preventDefault()}>
      <div className='grid gap-5 sm:grid-cols-2'>
        <div>
          <label htmlFor='role_name' className='block text-sm font-medium text-gray-700 dark:text-gray-300'>Role name <Required /></label>
          <input id='role_name' onKeyDown={keyPress} type='text' className={errors.name ? 'errors mt-1' : 'mt-1'} {...register('name', { required: true })} placeholder='Car Salesman / Annoying Sibling / Narrator / Wolverine' />
          { errors.name && <div className='mt-1 text-sm text-red-600'>{errors.name[0] || 'Nameless roles are hard to audition for.'}</div> }
          <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>Public. Looking for a <b>{kind}</b>.</p>
        </div>
      </div>

      <div>
        <label className='block text-sm font-medium text-gray-700 dark:text-gray-300'>What's the role?</label>
        <p className='mb-1 text-xs text-gray-500 dark:text-gray-400'>Who they are, what you need delivered, software, links. Is it a lead?</p>
        <TipTapTextarea html={characteristics} idx={tipTapIdx.current} breaksOnEnter
          updateHtml={(val) => setValue('characteristics', val, { shouldDirty: true, shouldValidate: true })}
          placeholder='A gruff bounty hunter with a soft spot for stray cats. Around 40 lines. Deliver as separate WAVs.' />
      </div>

      { AUDITION_KINDS.includes(kind) && <div>
        <label className='block text-sm font-medium text-gray-700 dark:text-gray-300'>Audition lines</label>
        <p className='mb-1 text-xs text-gray-500 dark:text-gray-400'>Up to three lines you want everyone to read. Leave blank and they'll pick their own.</p>
        <div className='space-y-2'>
          <input onKeyDown={keyPress} type='text' {...register('line1')} placeholder='With great power comes great responsibility.' />
          <input onKeyDown={keyPress} type='text' {...register('line2')} placeholder="Roads? Where we're going we don't need roads." />
          <input onKeyDown={keyPress} type='text' {...register('line3')} placeholder="So you're telling me there's a chance?" />
        </div>
      </div> }

      { !isNew && <div className='border-t border-gray-200 pt-4 dark:border-gray-700'>
        { !overridePayment && <div className='flex items-center gap-x-3 text-sm text-gray-700 dark:text-gray-200'>
          <span><b>Pay:</b> {paySummary} (same as the project)</span>
          <button type='button' className='font-semibold text-cccblue hover:underline' onClick={() => setOverridePayment(true)}>Set a different rate for this role</button>
        </div> }
        { overridePayment && <PaymentFields form={form} payment={payment} onPaymentChange={handlePaymentChange} tipTapIdx={tipTapIdx.current} subject='role' /> }
      </div> }

      <div className='flex flex-wrap items-center justify-end gap-2'>
        <span className='hidden text-xs text-gray-400 sm:inline'>⌘/ctrl + Enter saves</span>
        { closeModal && <DefaultButton onClick={closeModal} text={isNew ? 'Cancel' : 'Done'} /> }
        <PrimaryButton onClick={handleSubmit(onSubmit)} loading={loading} text={isNew ? 'Create role' : 'Save role'} />
      </div>
      { errors.base && <div className='text-sm text-red-600'>{errors.base[0]}</div> }
    </form>

    { !isNew && <div className='border-t border-gray-200 pt-4 dark:border-gray-700'>
      <RoleTags roleId={role.id} roleKind={role.kind} />
    </div> }
  </div>
}

export default RoleEdit

RoleEdit.propTypes = {
  initialRole: PropTypes.object.isRequired,
  projectId: PropTypes.number,
  updateRole: PropTypes.func,
  roleKind: PropTypes.string,
  closeModal: PropTypes.func,
  onCreated: PropTypes.func
}
